import { FC, useState } from 'react';
import { useNavigate } from 'react-router';
import { format } from 'date-fns';
import { Payment } from '@mui/icons-material';
import { TextField, Grid, Button, Typography, Box, Collapse } from '@mui/material';
import DisplayField from '@acorn-portal/shared/src/components/displayField';
import PaymentVendors from '@acorn-portal/shared/src/components/vendor/PaymentVendors';
import { DISPLAY_DATE } from '@acorn-portal/shared/src/utils/constants';
import { formatCurrency } from '@acorn-portal/shared/src/utils/formatter';
import { useAppDispatch } from '@acorn-portal/shared/src/hooks/reduxHook';
import { updatePayNowDetails } from '@acorn-portal/shared/src/store/paynow.slice';
import { ROUTE_PATHS } from '@acorn-portal/shared/src/utils/Routes';
import {
	GetPaymentDetailsResponseModel,
	GetPaymentCollectionOptionsResponseModel,
	PaymentPlanType,
	PayNowStatus,
} from '@acorn-portal/shared/src/services/api';
import {
	DescriptionSection,
	ImportantSection,
	NextStepsSection,
	ProceedSection,
} from '@acorn-portal/shared/src/pages/payNow/constants/MissedPaymentFormSections';
import {
	PaymentOptions,
	VaultCardOptOut,
	SplitPaymentInfo,
	ProceedCheckbox,
	PaymentPlanOptionsType,
} from '@acorn-portal/shared/src/components/payNow';
import useMissingPaymentForm, { IMissingPaymentFormValues } from '@acorn-portal/shared/src/hooks/useMissingPaymentForm';
import useFocusFirstError from '@acorn-portal/shared/src/hooks/useFocusFirstError';
import { SchemeType } from '@acorn-portal/shared/src/services/interface/Shared';

interface IProps {
	details: GetPaymentDetailsResponseModel;
	paymentCollection: GetPaymentCollectionOptionsResponseModel[];
	scheme?: SchemeType;
	onSubmit?: (values: IMissingPaymentFormValues) => void;
	customerEmail?: string;
}

const MissingPaymentForm: FC<IProps> = ({ onSubmit, details, customerEmail, paymentCollection, scheme }) => {
	const dispatch = useAppDispatch();
	const navigate = useNavigate();
	const [submitError, setSubmitError] = useState('');

	const formik = useMissingPaymentForm(
		{
			email: customerEmail ?? '',
			splitPaymentPlanType:
				paymentCollection.length > 0 ? paymentCollection[0].paymentPlanType : PaymentPlanType.PayInFull,
			proceed: false,
			vaultCardOptOut: false,
		},
		(values) => {
			try {
				if (onSubmit) {
					onSubmit(values);
					return;
				}
				dispatch(
					updatePayNowDetails({
						details,
						collectionOptions: paymentCollection,
						missingPaymentFormDetails: {
							...values,
							vaultCardOptOut: values.splitPaymentPlanType !== 'PayInFull' ? false : values.vaultCardOptOut,
						},
					}),
				);
				navigate(ROUTE_PATHS.processPayment);
			} catch (error) {
				console.error('Error during form submission:', error);
				setSubmitError('An error occurred during submission. Please try again.');
			}
		},
	);

	useFocusFirstError(formik);

	const missedPaymentDate = format(
		new Date(details?.missedPaymentDetails?.missedPaymentDate ?? new Date()),
		DISPLAY_DATE,
	);

	const isOverDue = details.status === PayNowStatus.DirectDebitOverdue;
	const initialAmount = formatCurrency(details?.missedPaymentDetails?.initialAmount ?? 0);

	const handlePaymentOptionsSelect = (_: React.ChangeEvent<HTMLInputElement>, value: PaymentPlanOptionsType) => {
		formik.setFieldValue('splitPaymentPlanType', value);
		formik.setFieldValue('proceed', false);
		if (value !== 'PayInFull') {
			formik.setFieldValue('vaultCardOptOut', false);
		}
	};

	return (
		<>
			<Box
				sx={(theme) => ({
					border: `1px solid ${theme.palette.divider}`,
					p: 2,
					borderRadius: 2,
				})}
			>
				<Typography variant='h6' color='secondary' sx={{ mb: 2 }}>
					Missed Payment Details
				</Typography>
				<DisplayField label='Date of missed payment'>{missedPaymentDate}</DisplayField>
				{isOverDue && (
					<>
						<DisplayField label='Outstanding missed payment'>{initialAmount}</DisplayField>
						<DisplayField label='Administration Fee'>{formatCurrency(paymentCollection[0].fee ?? 0)}</DisplayField>
					</>
				)}
				<DisplayField label='Total to pay'>{formatCurrency(paymentCollection[0].totalAmount ?? 0)}</DisplayField>
			</Box>

			<form onSubmit={formik.handleSubmit}>
				<TextField
					id='email'
					name='email'
					label='Email Address'
					variant='outlined'
					sx={{
						maxWidth: 600,
						marginTop: 3,
						textAlign: 'center',
						mx: 'auto',
						display: 'block',
						pb: 2,
					}}
					value={formik.values.email}
					onChange={formik.handleChange}
					error={formik.touched.email && Boolean(formik.errors.email)}
					helperText={
						(formik.touched.email && formik.errors.email) ||
						'Please enter your email address to receive payment confirmation.'
					}
					fullWidth
				/>
				<DescriptionSection scheme={scheme} paymentPlanVersion={details.currentPaymentPlanVersion ?? 2} />
				<Box textAlign='center' paddingTop={1} paddingBottom={2}>
					<PaymentOptions
						paymentCollection={paymentCollection}
						selectedPaymentType={formik.values.splitPaymentPlanType ?? 'PayInFull'}
						onChange={handlePaymentOptionsSelect}
					/>
				</Box>
				<Collapse in={formik.values.splitPaymentPlanType === 'PayInFull'}>
					<VaultCardOptOut
						checked={formik.values.vaultCardOptOut}
						onChange={formik.handleChange}
						error={formik.touched.vaultCardOptOut && Boolean(formik.errors.vaultCardOptOut)}
					/>
				</Collapse>
				<Collapse in={formik.values.splitPaymentPlanType !== 'PayInFull'}>
					<SplitPaymentInfo version={details.currentPaymentPlanVersion ?? 2} />
				</Collapse>
				<ImportantSection scheme={scheme} paymentPlanVersion={details.currentPaymentPlanVersion ?? 2} />
				<ProceedCheckbox
					checked={formik.values.proceed}
					onChange={formik.handleChange}
					error={formik.touched.proceed && Boolean(formik.errors.proceed)}
					isPaymentPlan={formik.values.splitPaymentPlanType !== 'PayInFull'}
					paymentPlanVersion={details.currentPaymentPlanVersion ?? 2}
				/>
				<Collapse in={formik.values.splitPaymentPlanType !== 'PayInFull'}>
					<ProceedSection scheme={scheme} paymentPlanVersion={details.currentPaymentPlanVersion ?? 2} />
				</Collapse>
				<NextStepsSection
					scheme={scheme}
					paymentPlanVersion={details.currentPaymentPlanVersion ?? 2}
					payInFull={formik.values.splitPaymentPlanType === 'PayInFull'}
				/>

				<Grid container sx={{ mt: 3 }} alignContent='center' textAlign='center' justifyContent='center'>
					<Grid size={{ xs: 12, sm: 6 }}>
						<Button variant='contained' fullWidth color='secondary' type='submit' startIcon={<Payment />} size='large'>
							Proceed To Payment
						</Button>
						{submitError && (
							<Typography textAlign='left' color='error'>
								{submitError}
							</Typography>
						)}
					</Grid>
				</Grid>
				<PaymentVendors />
			</form>
		</>
	);
};

export default MissingPaymentForm;
